import { Center, Loader, Stack, Text } from '@mantine/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useEffectiveDisplayUserRole } from '@/lib/displayUserRole';
import { canViewGroupDetail, readTherapyGroup } from '@/lib/groupTherapy';

import { GroupDetailPage } from './GroupDetailPage';

/**
 * Allows Practice Owner/Admin or a facilitator of this cohort to open Group detail.
 * List route (`/dashboard/groups`) remains admin-only.
 */
export function GroupDetailRoute() {
  const { groupId } = useParams<{ groupId: string }>();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const userRole = useEffectiveDisplayUserRole();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!groupId) {
      setAllowed(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const group = await readTherapyGroup(medplum, groupId);
        if (!cancelled) {
          setAllowed(group ? canViewGroupDetail(userRole, group, profile?.id) : false);
        }
      } catch {
        if (!cancelled) setAllowed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, medplum, profile?.id, userRole]);

  if (allowed === null) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Checking group access…</Text>
        </Stack>
      </Center>
    );
  }

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <GroupDetailPage />;
}
